// frontend/src/pages/admin/ManageProductsPage.tsx
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ShoppingBag, PlusCircle, MoreHorizontal, Search, Edit3, Trash2, Eye, Loader2, AlertCircle, FileWarning, LayoutDashboard } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { getProducts, deleteProduct as apiDeleteProduct } from '@/api/productService';
import { type ProductListed, type ProductStatus } from '@/types';

const ManageProductsPage = () => {
  const [products, setProducts] = useState<ProductListed[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const fetchAdminProducts = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Add status filter if you implement it in UI
      const fetchedProducts = await getProducts({ status: undefined }); 
      
      let productsToDisplay = fetchedProducts;
      if (searchTerm) {
        productsToDisplay = fetchedProducts.filter(product =>
          product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (product.description && product.description.toLowerCase().includes(searchTerm.toLowerCase()))
        );
      }
      setProducts(productsToDisplay);

    } catch (err: any) {
      if (err.response && err.response.status === 401) {
        setError("Unauthorized. Please log in again.");
        localStorage.removeItem('authToken'); // Clear token
        navigate('/login?sessionExpired=true');
      } else {
        setError("Failed to fetch products. Please try again later.");
      }
      console.error("Error fetching admin products:", err);
      setProducts([]);
    } finally {
      setIsLoading(false);
    }
  };
  
  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
        fetchAdminProducts();
    }, 300); // Debounce search/filter application
    return () => clearTimeout(debounceTimeout);
  }, [searchTerm, navigate]);


  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  };

  const getStatusBadgeVariant = (status: ProductStatus): "default" | "secondary" | "outline" | "destructive" => {
    switch (status) {
      case 'active': return 'default';
      case 'inactive': return 'outline';
      case 'coming_soon': return 'secondary';
      default: return 'secondary';
    }
  };

  const handleDeleteProduct = async (productId: number) => {
    if (!window.confirm("Are you sure you want to delete this product? This action cannot be undone.")) {
        return;
    }
    setIsDeleting(productId);
    setError(null);
    try {
      await apiDeleteProduct(productId);
      setProducts(prevProducts => prevProducts.filter(product => product.id !== productId));
      // alert("Product deleted successfully."); // Consider using a toast
    } catch (err: any) {
      console.error("Failed to delete product:", err);
      const errorMsg = err.response?.data?.detail || "Failed to delete product. Please try again.";
      setError(errorMsg);
      // alert(errorMsg); // Consider using a toast
    } finally {
      setIsDeleting(null);
    }
  };

  if (isLoading && products.length === 0) {
    return (
      <div className="container mx-auto px-4 py-20 flex flex-col items-center justify-center min-h-[60vh]">
        <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
        <p className="text-lg text-muted-foreground">Loading products...</p>
      </div>
    );
  }

  if (error && products.length === 0) {
    return (
      <div className="container mx-auto px-4 py-20 text-center min-h-[60vh] flex flex-col justify-center items-center">
        <FileWarning className="h-16 w-16 text-destructive mx-auto mb-6" />
        <h2 className="text-2xl font-semibold text-destructive mb-2">Error Loading Products</h2>
        <p className="text-muted-foreground mb-6">{error}</p>
        <Button onClick={fetchAdminProducts} variant="outline">Try Again</Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 md:py-12 space-y-8 text-foreground bg-background">
      <header className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-border/60 pb-6 relative">
        <div className="absolute bottom-0 left-0 w-1/3 h-px bg-gradient-to-r from-primary/70 via-accent/50 to-transparent"></div>
        <div className="flex items-center">
          <ShoppingBag className="h-10 w-10 text-primary mr-4 flex-shrink-0" />
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
              Manage <span className="text-gradient-maroon-to-gold">Products</span>
            </h1>
            <p className="mt-1 text-muted-foreground">
              Add, edit, and organize all company products and services.
            </p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 items-center">
            <Button asChild variant="outline" className="border-primary text-primary hover:bg-primary/10 hover:text-primary transition-colors self-start sm:self-center w-full sm:w-auto">
              <Link to="/admin/dashboard">
                <LayoutDashboard className="mr-2 h-4 w-4" /> Dashboard
              </Link>
            </Button>
            <Button asChild className="!bg-gradient-to-r !from-primary !to-accent hover:!from-primary/90 hover:!to-accent/90 text-primary-foreground shadow-md hover:shadow-lg transition-all duration-300">
            <Link to="/admin/products/new">
                <PlusCircle className="mr-2 h-5 w-5" /> Add New Product
            </Link>
            </Button>
        </div>
      </header>

      <div className="flex flex-col sm:flex-row items-center gap-4">
        <div className="relative w-full sm:max-w-sm">
          <Input
            type="search"
            placeholder="Search products by name or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 border-border focus:border-primary focus:ring-primary"
          />
          <Search className="absolute left-3 top-1/2 h-5 w-5 text-muted-foreground -translate-y-1/2" />
        </div>
      </div>
      
      {error && !isLoading && products.length > 0 && (
         <div className="p-3 bg-destructive/10 text-destructive border border-destructive/30 rounded-md flex items-center">
           <AlertCircle className="h-5 w-5 mr-2" /> {error}
         </div>
      )}

      <Card className="shadow-lg bg-card border border-border/30 pt-0">
        <CardContent className="p-0">
          <Table>
            <TableHeader className='bg-vedasMaroon-800'>
              <TableRow className="border-border/50">
                <TableHead className="w-[30%] text-primary-foreground py-4">Product Name</TableHead>
                <TableHead className="w-[35%] hidden md:table-cell text-primary-foreground">Short Description</TableHead>
                <TableHead className="text-primary-foreground">Status</TableHead>
                <TableHead className="text-primary-foreground">Last Updated</TableHead>
                <TableHead className="text-right text-primary-foreground">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
             {isLoading && products.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-16 text-muted-foreground">
                    <div className="flex flex-col items-center justify-center">
                        <Loader2 className="h-8 w-8 animate-spin text-primary mb-3" />
                        Loading products...
                    </div>
                  </TableCell>
                </TableRow>
              ) : products.length > 0 ? (
                products.map((product) => (
                  <TableRow key={product.id} className="border-border/40 hover:bg-muted/30 dark:hover:bg-muted/10">
                    <TableCell className="font-medium text-foreground">
                      <Link to={`/products/${product.slug}`} className="hover:text-primary hover:underline transition-colors">
                        {product.name}
                      </Link>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground hidden md:table-cell">
                        {product.description ? (product.description.length > 70 ? product.description.substring(0, 70) + '...' : product.description) : 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Badge variant={getStatusBadgeVariant(product.status)} className="capitalize">
                        {product.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{formatDate(product.updated_at)}</TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger
                          className="justify-self-end"
                          asChild
                        >
                          {isDeleting === product.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <MoreHorizontal className="h-4 w-4 cursor-pointer" />
                          )}
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-popover text-popover-foreground border-border">
                          <DropdownMenuLabel>Actions</DropdownMenuLabel>
                          <DropdownMenuItem asChild className="hover:bg-muted/50 dark:hover:bg-muted/20 focus:bg-muted/50 dark:focus:bg-muted/20 cursor-pointer">
                            <Link to={`/products/${product.slug}`} target="_blank" rel="noopener noreferrer">
                              <Eye className="mr-2 h-4 w-4 text-primary/80" /> View Live
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild className="hover:bg-muted/50 dark:hover:bg-muted/20 focus:bg-muted/50 dark:focus:bg-muted/20 cursor-pointer">
                            <Link to={`/admin/products/edit/${product.id}`}>
                              <Edit3 className="mr-2 h-4 w-4 text-primary/80" /> Edit
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuSeparator className="bg-border/50"/>
                          <DropdownMenuItem
                            onClick={() => handleDeleteProduct(product.id)}
                            className="text-destructive focus:text-destructive-foreground focus:bg-destructive/90 cursor-pointer"
                            disabled={isDeleting === product.id}
                          >
                            {isDeleting === product.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
                             Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-16 text-muted-foreground">
                    No products found.
                    {searchTerm && " Try adjusting your search or clearing it."}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default ManageProductsPage;